import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppLayout from "../../shared/components/AppLayout";
import patientService from "../../services/patientService";
import {
  CreateDoctorPatientPayload,
  GENDER_OPTIONS,
} from "../../types/patient";

const PatientEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "null");

  const [form, setForm] = useState<CreateDoctorPatientPayload>({
    first_name: "",
    last_name: "",
    gender: "",
    dob: "",
    address: "",
    phone: "",
    email: "",
  });
  const [errors, setErrors] = useState<Partial<CreateDoctorPatientPayload>>({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);


  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      try {
        const data = await patientService.fetchDoctorPatient(Number(id));
        setForm({
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          gender: data.gender || "",
          dob: data.dob || "",
          address: data.address || "",
          phone: data.phone || "",
          email: data.email?.includes("@placeholder.docfi")
            ? ""
            : data.email || "",
        });
      } catch (err: any) {
        setApiError(
          err.response?.data?.detail || err.message || "Failed to load patient",
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Matches create: only first name is required. Without this, a patient
  // created without a last name or gender could not be edited at all without
  // first supplying them.
  const validate = (): boolean => {
    const newErrors: Partial<CreateDoctorPatientPayload> = {};
    if (!form.first_name.trim())
      newErrors.first_name = "First name is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof CreateDoctorPatientPayload]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !id) return;

    setSubmitting(true);
    setApiError(null);

    try {
      const payload: CreateDoctorPatientPayload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        gender: form.gender,
        dob: form.dob || null,
        address: form.address || "",
        phone: form.phone || "",
        email: form.email || undefined,
      };
      await patientService.updateDoctorPatient(Number(id), payload);
      navigate(`/patients/${id}`);
    } catch (err: any) {
      const detail =
        err.response?.data?.detail ||
        err.response?.data?.email?.[0] ||
        Object.values(err.response?.data || {})[0] ||
        "Failed to update patient. Please try again.";
      setApiError(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <AppLayout active="patients">
          <div className="max-w-2xl mx-auto">
            <div className="mb-6">
              <button
                onClick={() => navigate(`/patients/${id}`)}
                className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1 mb-4"
              >
                ← Back to Patient Details
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Edit Patient</h1>
              <p className="text-gray-500 mt-1">
                Update the patient's record details.
              </p>
            </div>

            {apiError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {apiError}
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 space-y-6"
            >
              {/* Required fields */}
              <div>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                  Required Information
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      value={form.first_name}
                      onChange={handleChange}
                      placeholder="John"
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.first_name ? "border-red-400" : "border-gray-300"
                      }`}
                    />
                    {errors.first_name && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.first_name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      value={form.last_name}
                      onChange={handleChange}
                      placeholder="Doe"
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.last_name ? "border-red-400" : "border-gray-300"
                      }`}
                    />
                    {errors.last_name && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.last_name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Gender
                    </label>
                    <select
                      name="gender"
                      value={form.gender}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${
                        errors.gender ? "border-red-400" : "border-gray-300"
                      }`}
                    >
                      <option value="">Select gender...</option>
                      {GENDER_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {errors.gender && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.gender}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Optional fields */}
              <div>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                  Optional Information
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      name="dob"
                      value={form.dob || ""}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone || ""}
                      onChange={handleChange}
                      placeholder="+1 (555) 000-0000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email || ""}
                      onChange={handleChange}
                      placeholder="patient@example.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <textarea
                      name="address"
                      value={form.address || ""}
                      onChange={handleChange}
                      placeholder="123 Main St, New York, NY 10001"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => navigate(`/patients/${id}`)}
                  className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  {submitting ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
    </AppLayout>
  );
};

export default PatientEditPage;
